import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useOrgPermissions } from '@/hooks/useOrgPermissions'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Separator } from '@/components/ui/separator'
import {
  CreditCard, Users, CalendarDays, Newspaper, FileCheck,
  ExternalLink, CheckCircle, AlertCircle, Clock, Loader2,
  Share2, BarChart3,
} from 'lucide-react'

// ── helpers ────────────────────────────────────────────────────────────────

const PLAN_LABELS: Record<string, string> = {
  trial:        'Trial',
  individual:   'Individual',
  starter:      'Starter',
  professional: 'Professional',
  enterprise:   'Enterprise',
}

// Which of the three Conorbit services each plan includes (mirrors Pricing.tsx)
const PRESS_MODULE_KEYS = new Set(['media_registry','accreditation','events','awards','organogram','media_kits','journalist_portal'])
function planServices(modules: string[] | null | undefined) {
  const mods = modules ?? []
  return {
    social:    mods.includes('social_media'),
    press:     mods.some(m => PRESS_MODULE_KEYS.has(m)),
    analytics: mods.includes('coverage_analytics'),
  }
}

const SERVICE_BADGES = [
  { key: 'social' as const,    label: 'Social Hub',           Icon: Share2 },
  { key: 'press' as const,     label: 'Press & Journalist',   Icon: Newspaper },
  { key: 'analytics' as const, label: 'Analytics',            Icon: BarChart3 },
]

const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive'; icon: React.ReactNode }> = {
  active:     { label: 'Active',    variant: 'default',     icon: <CheckCircle className="w-3.5 h-3.5" /> },
  trialing:   { label: 'Trial',     variant: 'secondary',   icon: <Clock className="w-3.5 h-3.5" /> },
  past_due:   { label: 'Past Due',  variant: 'destructive', icon: <AlertCircle className="w-3.5 h-3.5" /> },
  canceled:   { label: 'Cancelled', variant: 'destructive', icon: <AlertCircle className="w-3.5 h-3.5" /> },
  incomplete: { label: 'Incomplete',variant: 'destructive', icon: <AlertCircle className="w-3.5 h-3.5" /> },
}

function formatCurrency(cents: number | null, currency = 'USD'): string {
  if (cents == null) return ''
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100)
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

function UsageMeter({
  icon,
  label,
  used,
  max,
}: {
  icon: React.ReactNode
  label: string
  used: number
  max: number
}) {
  const unlimited = max < 0
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / max) * 100))
  const near = !unlimited && pct >= 80

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          {icon}
          <span>{label}</span>
        </div>
        <span className={near ? 'text-amber-600 font-medium' : 'text-foreground font-medium'}>
          {used}{unlimited ? '' : ` / ${max}`}
        </span>
      </div>
      {!unlimited && (
        <Progress
          value={pct}
          className={`h-1.5 ${near ? '[&>div]:bg-amber-500' : ''}`}
        />
      )}
    </div>
  )
}

// ── component ──────────────────────────────────────────────────────────────

export function InstitutionBillingView() {
  const { orgId } = useOrgPermissions()

  const { data: summary, isLoading } = useQuery({
    queryKey: ['org-billing-summary', orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await (supabase
        .rpc('get_org_billing_summary', { p_org_id: orgId } as any) as any)
      if (error) throw error
      return data as {
        plan: string
        status: string
        billing_cycle: string | null
        payment_provider: string | null
        payment_method_label: string | null
        amount_cents: number | null
        currency: string
        next_payment_date: string | null
        notes: string | null
        usage: {
          journalists: number
          events: number
          press_releases: number
          accreditations: number
          team_members: number
        }
        transactions: Array<{
          id: string
          amount_cents: number
          currency: string
          status: string
          payment_provider: string
          plan: string | null
          billing_cycle: string | null
          invoice_url: string | null
          created_at: string
        }>
      }
    },
  })

  const { data: limits } = useQuery({
    queryKey: ['plan-limits', summary?.plan],
    enabled: !!summary?.plan,
    queryFn: async () => {
      const { data } = await (supabase
        .from('plan_limits' as any)
        .select('*')
        .eq('plan', summary!.plan)
        .single() as any)
      return data as {
        plan: string
        max_journalists: number
        max_events: number
        max_team: number
        max_press_releases: number
        price_usd_monthly: number
        features: string[]
        modules: string[]
      } | null
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading billing information…
      </div>
    )
  }

  if (!summary) return null

  const statusCfg = STATUS_CONFIG[summary.status] ?? STATUS_CONFIG.active
  const plan = PLAN_LABELS[summary.plan] ?? summary.plan

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-2xl font-bold">Billing</h2>
        <p className="text-justify text-muted-foreground text-sm mt-1">
          Your current plan, usage, and payment history.
        </p>
      </div>

      {/* Plan + payment card */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-muted-foreground" />
              Current Plan
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <span className="text-3xl font-bold">{plan}</span>
              <Badge variant={statusCfg.variant} className="flex items-center gap-1">
                {statusCfg.icon}
                {statusCfg.label}
              </Badge>
            </div>

            {summary.amount_cents != null && (
              <div className="text-sm text-muted-foreground">
                {formatCurrency(summary.amount_cents, summary.currency)}
                {summary.billing_cycle ? ` / ${summary.billing_cycle}` : ''}
              </div>
            )}

            {summary.payment_method_label && (
              <div className="text-sm text-muted-foreground">
                Payment method: <span className="text-foreground">{summary.payment_method_label}</span>
              </div>
            )}

            {summary.next_payment_date && (
              <div className="text-sm text-muted-foreground">
                Next payment: <span className="text-foreground">{formatDate(summary.next_payment_date)}</span>
              </div>
            )}

            {/* Service badges */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {SERVICE_BADGES.map(({ key, label, Icon }) => {
                const active = planServices(limits?.modules)[key]
                return (
                  <span
                    key={key}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                      active
                        ? 'bg-primary/10 text-primary border-primary/30'
                        : 'text-muted-foreground/50 border-border line-through'
                    }`}
                  >
                    <Icon className="w-3 h-3" /> {label}
                  </span>
                )
              })}
            </div>

            {summary.plan !== 'enterprise' && (
              <Button variant="outline" size="sm" className="mt-2 w-full" asChild>
                <Link to="/pricing">Upgrade Plan</Link>
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Included features */}
        {limits && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Plan Includes</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1.5">
                {limits.features.map(f => (
                  <li key={f} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Usage meters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Usage</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <UsageMeter
            icon={<Users className="w-4 h-4" />}
            label="Journalists"
            used={summary.usage.journalists}
            max={limits?.max_journalists ?? -1}
          />
          <UsageMeter
            icon={<CalendarDays className="w-4 h-4" />}
            label="Events"
            used={summary.usage.events}
            max={limits?.max_events ?? -1}
          />
          <UsageMeter
            icon={<Newspaper className="w-4 h-4" />}
            label="Press Releases"
            used={summary.usage.press_releases}
            max={limits?.max_press_releases ?? -1}
          />
          <UsageMeter
            icon={<FileCheck className="w-4 h-4" />}
            label="Active Accreditations"
            used={summary.usage.accreditations}
            max={-1}
          />
          <UsageMeter
            icon={<Users className="w-4 h-4" />}
            label="Team Members"
            used={summary.usage.team_members}
            max={limits?.max_team ?? -1}
          />
        </CardContent>
      </Card>

      {/* Payment history */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Payment History</CardTitle>
        </CardHeader>
        <CardContent>
          {summary.transactions.length === 0 ? (
            <p className="text-justify text-sm text-muted-foreground text-center py-6">No payments on record yet.</p>
          ) : (
            <div className="space-y-0">
              {summary.transactions.map((tx, i) => (
                <div key={tx.id}>
                  {i > 0 && <Separator />}
                  <div className="flex items-center justify-between py-3">
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium">
                        {formatCurrency(tx.amount_cents, tx.currency)}
                        {tx.plan && (
                          <span className="text-muted-foreground font-normal">
                            {' '}: {PLAN_LABELS[tx.plan] ?? tx.plan}
                            {tx.billing_cycle ? ` (${tx.billing_cycle})` : ''}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatDate(tx.created_at)} · {tx.payment_provider}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          tx.status === 'completed' ? 'default'
                          : tx.status === 'pending'   ? 'secondary'
                          : 'destructive'
                        }
                        className="text-xs"
                      >
                        {tx.status}
                      </Badge>
                      {tx.invoice_url && (
                        <a
                          href={tx.invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-foreground transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {summary.notes && (
        <p className="text-justify text-xs text-muted-foreground border rounded-md px-3 py-2">{summary.notes}</p>
      )}
    </div>
  )
}
